import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import {
  describeMyAffiliateEmptyState,
  fetchMyAffiliatePayments,
  fetchMyAffiliatePrograms,
  formatMyAffiliateCurrency,
} from '@/lib/my-affiliate'
import { MyAffiliateEmptyState } from '@/pages/SouAfiliadoPage/MyAffiliateEmptyState'
import type { MyAffiliatePaymentItem, MyAffiliateProgram } from '@/types/my-affiliate'
import styles from './SouAfiliadoPage.module.css'

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function SouAfiliadoVendasPage() {
  const [programs, setPrograms] = useState<MyAffiliateProgram[]>([])
  const [emptyMessage, setEmptyMessage] = useState(
    describeMyAffiliateEmptyState(undefined, null, [])
  )
  const [selectedId, setSelectedId] = useState<number | ''>('')
  const [items, setItems] = useState<MyAffiliatePaymentItem[]>([])
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<{
    current_page: number
    last_page: number
    total: number
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadPayments = useCallback(async (affiliateId: number, p: number) => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchMyAffiliatePayments(affiliateId, { page: p, per_page: 20 })
      setItems(res.items)
      setPagination({
        current_page: res.pagination.current_page,
        last_page: res.pagination.last_page,
        total: res.pagination.total,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar vendas.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setIsLoading(true)
      setError(null)
      try {
        const res = await fetchMyAffiliatePrograms()
        if (cancelled) return
        setPrograms(res.programs)
        setEmptyMessage(
          describeMyAffiliateEmptyState(res.login_context, res.profile, res.programs)
        )
        if (res.programs.length > 0) {
          setSelectedId((prev) =>
            prev === '' || !res.programs.some((p) => p.affiliate_id === prev)
              ? res.programs[0].affiliate_id
              : prev
          )
        } else {
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar programas.')
          setIsLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (selectedId === '') {
      setItems([])
      setPagination(null)
      setIsLoading(false)
      return
    }
    void loadPayments(selectedId, page)
  }, [selectedId, page, loadPayments])

  const handleProgramChange = (id: number | '') => {
    setSelectedId(id)
    setPage(1)
  }

  if (!isLoading && programs.length === 0 && !error) {
    return (
      <MyAffiliateEmptyState icon="mdi:receipt-text-outline" message={emptyMessage} />
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.filterRow}>
        <label className={styles.filterLabel} htmlFor="program-select">
          Programa / instituição
        </label>
        <select
          id="program-select"
          className={styles.select}
          value={selectedId === '' ? '' : String(selectedId)}
          onChange={(e) => {
            const v = e.target.value
            handleProgramChange(v === '' ? '' : Number.parseInt(v, 10))
          }}
        >
          {programs.map((p) => (
            <option key={p.affiliate_id} value={p.affiliate_id}>
              {p.institution.name}
              {!p.is_active ? ' (inativo)' : ''}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <div className={styles.centered}>
          <span>{error}</span>
          <button
            type="button"
            className={styles.retryBtn}
            onClick={() => selectedId !== '' && void loadPayments(selectedId, page)}
          >
            Tentar novamente
          </button>
        </div>
      ) : isLoading ? (
        <div className={styles.centered}>
          <Icon icon="mdi:loading" className={styles.spinner} />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.emptyBlock}>
          <Icon icon="mdi:receipt-text-remove-outline" />
          <span>Nenhuma venda paga atribuída neste programa.</span>
        </div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Campanha</th>
                  <th>Comprador</th>
                  <th>Valor</th>
                  <th>Comissão</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.payment_id}>
                    <td>{formatDate(item.paid_at)}</td>
                    <td>{item.campaign.title?.trim() || item.campaign.name}</td>
                    <td>{item.buyer.name}</td>
                    <td>{formatMyAffiliateCurrency(item.payment_amount)}</td>
                    <td>{formatMyAffiliateCurrency(item.commission_amount)}</td>
                    <td>{item.affiliate.commission_percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.last_page > 1 ? (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span>
                Página {pagination.current_page} de {pagination.last_page} ({pagination.total}{' '}
                vendas)
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page >= pagination.last_page}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
